import { RasterMetadata } from '@carto/api-client'
import { LayerBaseConfig } from '@carto/kepler.gl/layers'

import { getDatasetType, getSchemeFromTilesetDataset } from 'features/builder/state/builderSliceUtils'
import {
  KeplerLayerConfig,
  KeplerLayerVisConfig,
  KeplerTable,
  KeplerVisualChannel,
  LayerTypes,
  RasterStyleType,
  KeplerScaleType,
  TilingScheme,
  KeplerColorRange,
  KeplerLayerMeta,
  RasterLayerConfigColorBand,
  KeplerColorMap
} from 'features/builder/types'
import { MapType } from 'features/maps/types'
import TileLayer from './TileLayer'
import { createBandDomain, EMPTY_COLOR_RGBA, validateRasterLayerConfig } from '../../layers/raster/rasterLayerConfig'
import { visualChannelsConfigs } from './visualChannels'
import { ColorRange } from 'features/account/types'

/**
 * RasterLayer config is structured as follows
 *
 * Fields reused from common layers
 *  * `config.colorField` - selected band for ColorRange and UniqueValues
 *  * `visConfig.colorRange` - color range in ColorRange mode
 *  * `config.colorScale` - color scale type in ColorRange mode
 *  * `config.colorDomain` - configured color domain
 *
 *
 * Specific fields
 *  * `visConfig.rasterStyleType` enum of `RasterStyleType`
 *  * `visConfig.uniqueValuesColorRange` - color range for UniqueValues mode
 *  * `visConfig.colorBands` (RGB only) array of configured color bands or expressions
 *  * `config.uniqueValuesColorScale - - scale type for UniqueValues mode, always ordinal
 *  * `config.uniqueValuesColorDomain` - domain for UniqueValues mode
 */
const VIS_CONFIG_SETTINGS = {
  opacity: 'opacity',
  colorRange: 'colorRange',
  rasterStyleType: {
    type: 'text',
    defaultValue: RasterStyleType.ColorRange,
    property: 'rasterStyleType'
  },
  colorBands: {
    type: 'object',
    defaultValue: [],
    property: 'colorBands'
  },
  uniqueValuesColorRange: {
    type: 'color-range-select',
    defaultValue: undefined,
    property: 'uniqueValuesColorRange'
  }
}

interface RasterTileLayerVisConfig extends KeplerLayerVisConfig {
  rasterStyleType: RasterStyleType
  colorBands?: RasterLayerConfigColorBand[]
}

type RasterTileLayerConfig = LayerBaseConfig &
  KeplerLayerConfig & {
    visConfig: RasterTileLayerVisConfig
  }

interface RasterTileLayerMeta extends KeplerLayerMeta {
  rasterMetadata?: RasterMetadata
}

export default class RasterTileLayer extends TileLayer {
  declare meta: RasterTileLayerMeta
  declare config: RasterTileLayerConfig

  constructor(props = {}) {
    super(props)
    this.registerVisConfig(VIS_CONFIG_SETTINGS)
  }

  get visualChannels(): Record<string, KeplerVisualChannel> {
    /**
     * Now, we have two "classic" coloring schemes available for RasterLayer
     *  * ColorRange
     *  * UniqueValue
     *
     * Both are just color schemes, so we could use the same visual channel for both, but then
     * if we switch between them then we get incompatible color ranges and scale settings ... and
     * weird state, where ColorRange palette propagates to Unique Style palette (and vice versa).
     *
     * Solution is to just use two different visual channels for these two styles - this mostly fits
     * kepler-based config model.
     *
     * Alternative would be to "stash" color/style when switching to another style, but that would need
     * monkey patching config and that would be complex and error prone too.
     */
    return {
      // standrad color for ColorRange raster layer style
      color: {
        ...visualChannelsConfigs.color,
        accessor: 'getFillColor',
        condition: (layerConfig) => layerConfig.visConfig.rasterStyleType === RasterStyleType.ColorRange,
        nullValue: EMPTY_COLOR_RGBA,
        getAttributeValue: (config: KeplerLayerConfig) => config.color,
        defaultValue: (config: KeplerLayerConfig) => config.color
      },
      //
      uniqueValuesColor: {
        key: 'uniqueValuesColor',
        property: 'uniqueValuesColor',
        field: 'colorField',
        scale: 'uniqueValuesColorScale',
        domain: 'uniqueValuesColorDomain',
        range: 'uniqueValuesColorRange',
        channelScaleType: 'color',
        accessor: 'getFillColor',
        condition: (layerConfig) => layerConfig.visConfig.rasterStyleType === RasterStyleType.UniqueValues,
        nullValue: EMPTY_COLOR_RGBA,
        getAttributeValue: (config: KeplerLayerConfig) => config.color,
        defaultValue: (config: KeplerLayerConfig) => config.color
      }
    }
  }

  public get type() {
    return LayerTypes.Raster
  }

  get name() {
    return 'Raster'
  }

  getDefaultLayerConfig(props = {}) {
    const baseConfig = super.getDefaultLayerConfig(props)
    return {
      ...baseConfig,
      uniqueValuesColorScale: KeplerScaleType.Ordinal
    }
  }

  static findDefaultLayerProps(dataset: KeplerTable, foundLayers) {
    const emptyProps = { props: [] }

    if (foundLayers?.length) {
      return emptyProps
    }
    const scheme = getSchemeFromTilesetDataset(dataset)

    const rasterMetadata = dataset.metadata.raster_metadata
    if (getDatasetType(dataset) === MapType.raster && scheme === TilingScheme.Quadbin && rasterMetadata) {
      const defaultRasterLayerConfig = validateRasterLayerConfig({
        layerConfig: { visConfig: {} } as KeplerLayerConfig,
        rasterMetadata,
        fields: dataset.fields
      })
      return {
        props: [
          {
            ...defaultRasterLayerConfig,
            label: dataset.label,
            isVisible: true,
            columns: {}
          }
        ]
      }
    }
    return emptyProps
  }

  getScaleOptions(channelKey: string) {
    if (channelKey === 'uniqueValuesColor') {
      return [KeplerScaleType.Ordinal, 'custom']
    }
    if (channelKey === 'color') {
      const rasterMetadata = this.meta.rasterMetadata as RasterMetadata | undefined
      const colorBandInfo = rasterMetadata?.bands.find(({ name }) => name === this.config.colorField?.id)

      // Some old rasters here and there don't have  quantiles in stats.
      // Disable quantile scale for them.
      // OTOH, getScaleOptions is called even if there is no raster metadata, so we need to return true in that case.
      const hasQuantiles = rasterMetadata ? Boolean(colorBandInfo?.stats?.quantiles) : true

      // Logarithmic is inserted always by NumericColorDimensionScaleSelector
      return [...(hasQuantiles ? [KeplerScaleType.Quantile] : []), KeplerScaleType.Quantize, 'custom']
    } else {
      return []
    }
  }

  calculateLayerDomain(dataset: KeplerTable, visualChannel: KeplerVisualChannel) {
    const { scale: scalePropName, range: rangeFieldName, field: fieldPropName } = visualChannel

    const config = this.config as KeplerLayerConfig
    const { visConfig, [scalePropName]: scaleType, [fieldPropName]: field } = config

    const rasterMetadata: RasterMetadata | undefined = dataset?.metadata?.raster_metadata as RasterMetadata
    const colorRange = visConfig[rangeFieldName as string] as KeplerColorRange | undefined

    const bandInfo = rasterMetadata.bands.find((band) => band.name === field?.id)

    if (!field || !rasterMetadata || !colorRange || !bandInfo) return [0, 1]

    return createBandDomain({ scaleType, colorRange, stats: bandInfo.stats })
  }

  neededAttributesAreAvailable() {
    return true
  }

  get useOpacityPerChannel() {
    return false
  }

  formatLayerData(datasets: Record<string, KeplerTable>, oldLayerData: unknown) {
    const keplerDataset = datasets[this.config.dataId]
    const { metadata } = keplerDataset
    const { raster_metadata: rasterMetadata } = metadata

    if (!keplerDataset || !rasterMetadata) return oldLayerData

    this.updateMeta({ rasterMetadata })
  }

  updateColorMap(prop: 'uniqueValuesColorRange' | 'colorRange', colorMap: KeplerColorMap, colorRange: ColorRange) {
    if (prop === 'uniqueValuesColorRange') {
      return this.updateCategoryColorMap(colorMap, this.config.uniqueValuesColorDomain, colorRange)
    } else {
      return this.updateNumericColorMap(colorMap, this.config.colorDomain, colorRange)
    }
  }
}
